import { useEffect, useMemo, useState } from "react";
import { cn } from "@/utils/cn";
import HistoryIcon from "@mui/icons-material/History";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import type { SvgIconComponent } from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import { NavLinkRow } from "@/components/layout/SidebarNavRows";
import { Tooltip } from "@/components/ui/Tooltip";
import {
  resolveGroupIcon,
  SidebarNavFlyout,
} from "@/components/layout/SidebarNavFlyout";

export interface NavItem {
  to: string;
  label: string;
  icon: SvgIconComponent;
  end?: boolean;
  /** Required feature code (org tenant) — shown when module is on; locked if missing */
  feature?: string;
}

/** @deprecated Use NavWorkflowGroup for per-app sidebars */
export interface NavSection {
  id: string;
  label: string;
  module?: string;
  items: NavItem[];
}

/** Business workflow group within a single application sidebar */
export interface NavWorkflowGroup {
  id: string;
  label: string;
  items: NavItem[];
  /** Dashboard group — always visible, no collapse */
  pinned?: boolean;
}

interface SidebarProps {
  items?: NavItem[];
  /** @deprecated Use workflowGroups */
  sections?: NavSection[];
  workflowGroups?: NavWorkflowGroup[];
  /** Quick access — recent routes in this workspace */
  recentItems?: NavItem[];
  favoriteItems?: NavItem[];
  collapsed: boolean;
  /** Primary sidebar heading — usually the active module/workspace name */
  brand?: string;
  /** Secondary sidebar line — short scope summary related to the heading */
  appLabel?: string;
  /** Active nav highlight — brand primary only in shell */
  accent?: "violet" | "slate";
  hasFeature?: (code: string) => boolean;
}

const QUICK_ACCESS_ICONS: Record<string, SvgIconComponent> = {
  favorites: StarBorderIcon,
  recent: HistoryIcon,
};

const GROUP_ICON_OVERRIDES: Record<string, SvgIconComponent> = {
  dashboard: HomeOutlinedIcon,
  administration: SettingsIcon,
};

function pathMatchesItem(pathname: string, to: string): boolean {
  const base = to.split("?")[0];
  return (
    pathname === base ||
    (base !== "/pos/dashboard" && pathname.startsWith(`${base}/`))
  );
}

function groupIsActive(pathname: string, items: NavItem[]): boolean {
  return items.some((item) => pathMatchesItem(pathname, item.to));
}

function WorkflowGroupBlock({
  group,
  collapsed,
  expanded,
  onToggle,
  hasFeature,
  accent,
  pathname,
}: {
  group: NavWorkflowGroup;
  collapsed: boolean;
  expanded: boolean;
  onToggle: () => void;
  hasFeature?: (code: string) => boolean;
  accent: NonNullable<SidebarProps["accent"]>;
  pathname: string;
}) {
  if (group.items.length === 0) return null;

  const isPinned = group.pinned || group.id === "dashboard";
  const active = groupIsActive(pathname, group.items);
  const GroupIcon = resolveGroupIcon(group, GROUP_ICON_OVERRIDES);

  const groupLabelContent = (
    <span className="flex min-w-0 items-center gap-1.5">
      <GroupIcon sx={{ fontSize: 14 }} className="shrink-0 opacity-70" />
      <span className="truncate">{group.label}</span>
    </span>
  );

  if (collapsed) {
    if (group.items.length === 1) {
      const item = group.items[0];
      const groupIcon = resolveGroupIcon(group, GROUP_ICON_OVERRIDES);
      return (
        <div className="workspace-nav-group">
          <NavLinkRow
            item={item}
            collapsed
            iconOverride={
              GROUP_ICON_OVERRIDES[group.id] ? groupIcon : undefined
            }
            locked={Boolean(
              item.feature && hasFeature && !hasFeature(item.feature!),
            )}
            accent={accent}
          />
        </div>
      );
    }
    return (
      <div className="workspace-nav-group">
        <SidebarNavFlyout
          label={group.label}
          icon={resolveGroupIcon(group, GROUP_ICON_OVERRIDES)}
          items={group.items}
          accent={accent}
          active={active}
          hasFeature={hasFeature}
        />
      </div>
    );
  }

  return (
    <div className="workspace-nav-group">
      {!isPinned && (
        <button
          type="button"
          onClick={onToggle}
          className="workspace-nav-group-label"
        >
          {groupLabelContent}
          <span className="workspace-nav-group-chevron">
            {expanded ? "−" : "+"}
          </span>
        </button>
      )}
      {isPinned && group.items.length > 1 && (
        <div className="workspace-nav-group-label workspace-nav-group-label--static">
          {groupLabelContent}
        </div>
      )}
      {(expanded || isPinned) && (
        <div className={cn("workspace-nav-group-items", !isPinned && "mt-0.5")}>
          {group.items.map((item) => (
            <NavLinkRow
              key={item.to}
              item={item}
              collapsed={false}
              locked={Boolean(
                item.feature && hasFeature && !hasFeature(item.feature),
              )}
              accent={accent}
            />
          ))}
        </div>
      )}
      {!isPinned && expanded && <div className="workspace-nav-divider" />}
    </div>
  );
}

function QuickAccessBlock({
  id,
  label,
  items,
  collapsed,
  accent,
  pathname,
}: {
  id: string;
  label: string;
  items: NavItem[];
  collapsed: boolean;
  accent: NonNullable<SidebarProps["accent"]>;
  pathname: string;
}) {
  if (items.length === 0) return null;

  const HeaderIcon = QUICK_ACCESS_ICONS[id] ?? items[0]?.icon;

  const quickLabel = (
    <span className="flex min-w-0 items-center gap-1.5">
      {HeaderIcon ? (
        <HeaderIcon sx={{ fontSize: 14 }} className="shrink-0 opacity-70" />
      ) : null}
      <span className="truncate">{label}</span>
    </span>
  );

  if (collapsed) {
    return (
      <div className="workspace-nav-group mb-2">
        <SidebarNavFlyout
          label={label}
          icon={QUICK_ACCESS_ICONS[id] ?? items[0].icon}
          items={items}
          accent={accent}
          active={groupIsActive(pathname, items)}
        />
      </div>
    );
  }

  return (
    <div className="workspace-nav-group mb-3">
      <div className="workspace-nav-group-label workspace-nav-group-label--static">
        {quickLabel}
      </div>
      <div className="workspace-nav-group-items">
        {items.map((item) => (
          <NavLinkRow
            key={`${label}-${item.to}`}
            item={item}
            collapsed={false}
            accent={accent}
          />
        ))}
      </div>
      <div className="workspace-nav-divider" />
    </div>
  );
}

export function Sidebar({
  items = [],
  sections = [],
  workflowGroups = [],
  recentItems = [],
  favoriteItems = [],
  collapsed,
  appLabel = "",
  brand = "",
  accent = "violet",
  hasFeature,
}: SidebarProps) {
  const location = useLocation();
  const useWorkflow = workflowGroups.length > 0;
  const useSections = !useWorkflow && sections.length > 0;

  const activeGroupId = useMemo(() => {
    if (!useWorkflow) return null;
    const match = workflowGroups.find((g) =>
      groupIsActive(location.pathname, g.items),
    );
    return match?.id ?? "dashboard";
  }, [location.pathname, useWorkflow, workflowGroups]);

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!activeGroupId) return;
    setExpanded((prev) => ({ ...prev, [activeGroupId]: true }));
  }, [activeGroupId]);

  const toggleGroup = (id: string) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const brandInitial = (brand || "Fattah").charAt(0).toUpperCase();
  const headerTitle = [brand, appLabel].filter(Boolean).join(" — ");

  return (
    <aside
      className={cn(
        "workspace-sidebar",
        collapsed && "workspace-sidebar--collapsed",
      )}
    >
      <div className="workspace-sidebar-header">
        {collapsed ? (
          <Tooltip content={headerTitle || brand}>
            <span className="workspace-sidebar-logo">{brandInitial}</span>
          </Tooltip>
        ) : (
          <div className="min-w-0 flex-1">
            {brand && (
              <p className="workspace-sidebar-brand truncate">{brand}</p>
            )}
            {appLabel && (
              <p className="workspace-sidebar-app truncate">{appLabel}</p>
            )}
          </div>
        )}
      </div>

      <nav className="workspace-sidebar-nav" aria-label="Workspace navigation">
        <QuickAccessBlock
          id="favorites"
          label="Favorites"
          items={favoriteItems}
          collapsed={collapsed}
          accent={accent}
          pathname={location.pathname}
        />
        <QuickAccessBlock
          id="recent"
          label="Recent"
          items={recentItems}
          collapsed={collapsed}
          accent={accent}
          pathname={location.pathname}
        />
        {useWorkflow
          ? workflowGroups.map((group) => (
              <WorkflowGroupBlock
                key={group.id}
                group={group}
                collapsed={collapsed}
                expanded={
                  collapsed
                    ? false
                    : (expanded[group.id] ??
                      group.pinned ??
                      group.id === "dashboard")
                }
                onToggle={() => toggleGroup(group.id)}
                hasFeature={hasFeature}
                accent={accent}
                pathname={location.pathname}
              />
            ))
          : useSections
            ? sections.map((section) => (
                <WorkflowGroupBlock
                  key={section.id}
                  group={{
                    id: section.id,
                    label: section.label,
                    items: section.items,
                  }}
                  collapsed={collapsed}
                  expanded={collapsed ? false : (expanded[section.id] ?? true)}
                  onToggle={() => toggleGroup(section.id)}
                  hasFeature={hasFeature}
                  accent={accent}
                  pathname={location.pathname}
                />
              ))
            : items.map((item) => (
                <NavLinkRow
                  key={item.to}
                  item={item}
                  collapsed={collapsed}
                  accent={accent}
                />
              ))}
      </nav>
    </aside>
  );
}
